import { useCallback, useEffect, useState } from "react";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { fmtFecha } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";

const fmtVal = (v) => (typeof v === "boolean" ? (v ? "Activado" : "Desactivado") : String(v ?? "—"));

const SettingRow = ({ item, canEdit, onSave }) => {
  const isJson = item.tipo === "json";
  const isList = item.tipo === "lista_paises";
  const [val, setVal] = useState(isJson ? JSON.stringify(item.valor, null, 1) : isList ? item.valor.join(", ") : item.valor);
  const editable = canEdit && item.editable;
  const dirty = isJson ? val !== JSON.stringify(item.valor, null, 1) : isList ? val !== item.valor.join(", ") : val !== item.valor;
  const save = () => {
    if (item.clave === "biometria_retencion_dias" && !window.confirm(`Cambiar la retención de biometría a ${val} días afecta a TODOS los clientes de esta empresa: las selfies y videos más antiguos se borrarán (destrucción de clave, irreversible) en la próxima ejecución diaria. Queda en la bitácora. ¿Confirmas?`)) return;
    if (isList) return onSave(item.clave, val.split(",").map((x) => x.trim().toUpperCase()).filter(Boolean));
    if (!isJson) return onSave(item.clave, val);
    try {
      onSave(item.clave, JSON.parse(val));
    } catch {
      onSave(item.clave, "__json_invalido__");
    }
  };
  return (
    <TableRow data-testid={`setting-row-${item.clave}`}>
      <TableCell>
        <p className="font-mono text-xs text-slate-900">{item.clave}</p>
        <p className="text-xs text-slate-500 max-w-md">{item.descripcion}</p>
      </TableCell>
      <TableCell>
        {item.tipo === "bool" ? (
          <div className="flex items-center gap-2">
            <Switch checked={!!val} disabled={!editable} onCheckedChange={setVal} data-testid={`setting-input-${item.clave}`} />
            {item.bloqueado && <span className="inline-flex items-center gap-1 text-xs text-amber-700"><Lock size={12} /> Fase posterior</span>}
          </div>
        ) : isJson ? (
          <textarea className="w-72 h-40 font-mono text-[11px] border rounded-md p-2" value={val} disabled={!editable}
            onChange={(e) => setVal(e.target.value)} data-testid={`setting-input-${item.clave}`} />
        ) : item.tipo === "opcion" ? (
          <select className="h-9 border rounded-md px-2 text-sm" value={val} disabled={!editable} onChange={(e) => setVal(e.target.value)} data-testid={`setting-input-${item.clave}`}>
            {(item.opciones || []).map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : item.tipo === "decimal" || isList ? (
          <Input inputMode={isList ? "text" : "decimal"} className={isList ? "w-48 h-9" : "w-24 h-9"} value={val} disabled={!editable}
            onChange={(e) => setVal(isList ? e.target.value : e.target.value.replace(",", "."))} data-testid={`setting-input-${item.clave}`} />
        ) : (
          <Input type="number" className="w-24 h-9" value={val} min={item.min} max={item.max} disabled={!editable}
            onChange={(e) => setVal(e.target.value === "" ? "" : Number(e.target.value))} data-testid={`setting-input-${item.clave}`} />
        )}
      </TableCell>
      <TableCell className="text-xs text-slate-600">
        v{item.version}{item.heredado ? " · global" : ""}
        <div>{fmtFecha(item.fecha)}</div>
        <div>{item.cambiado_por_correo}</div>
      </TableCell>
      <TableCell>
        {editable && (
          <Button size="sm" disabled={!dirty} onClick={save} data-testid={`setting-save-${item.clave}`}>Guardar</Button>
        )}
      </TableCell>
    </TableRow>
  );
};

export const SettingsView = ({ base, canEdit }) => {
  const [items, setItems] = useState([]);
  const [hist, setHist] = useState([]);

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([api.get(`${base}/settings`), api.get(`${base}/settings/history`)]);
    setItems(a.data.items);
    setHist(b.data.items);
  }, [base]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (clave, valor) => {
    try {
      await api.put(`${base}/settings/${clave}`, { valor });
      toast.success("Configuración guardada");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-8" data-testid="settings-view">
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <Table data-testid="settings-table">
          <TableHeader>
            <TableRow>{["Parámetro", "Valor", "Versión", ""].map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow>
          </TableHeader>
          <TableBody>
            {items.map((i) => <SettingRow key={`${i.clave}-${i.version}`} item={i} canEdit={canEdit} onSave={save} />)}
          </TableBody>
        </Table>
      </div>
      <div className="space-y-3">
        <h2 className="font-heading text-lg font-semibold text-slate-900">Historial de cambios</h2>
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <Table data-testid="settings-history-table">
            <TableHeader>
              <TableRow>{["Fecha", "Parámetro", "Anterior", "Nuevo", "Versión", "Cambiado por"].map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow>
            </TableHeader>
            <TableBody>
              {hist.map((h) => (
                <TableRow key={h.id}>
                  <TableCell className="text-sm whitespace-nowrap">{fmtFecha(h.fecha)}</TableCell>
                  <TableCell className="font-mono text-xs">{h.clave}</TableCell>
                  <TableCell className="text-sm">{fmtVal(h.valor_anterior)}</TableCell>
                  <TableCell className="text-sm font-medium">{fmtVal(h.valor)}</TableCell>
                  <TableCell className="text-xs">v{h.version}</TableCell>
                  <TableCell className="text-xs">{h.cambiado_por_correo}</TableCell>
                </TableRow>
              ))}
              {!hist.length && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">Sin cambios propios todavía (se usan los valores globales)</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};
